import { Add, Remove } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF, GridColDef, GridRowsProp } from '@mui/x-data-grid-pro';

import { PanelGrid } from 'components/grid';
import { Block } from 'components/page';
import { ListagemProvider } from 'context/ListagemContext';
import { GerarReembolsoStatusChip } from 'pages/financeiro/reembolso/gerar-reembolso/components';
import {
    ManutencaoReembolsoDownload,
    ManutencaoReembolsoFalhasGrid,
    ManutencaoReembolsoReverterButton,
} from 'pages/financeiro/reembolso/manutencao-reembolso/components';
import { useQueryApuracoes } from 'pages/financeiro/reembolso/manutencao-reembolso/hooks';
import { Apuracao } from 'pages/financeiro/reembolso/manutencao-reembolso/types';
import { brlDate, brlDateTime } from 'util/format';

/**
 * Retorna array com os dados formatados para o grid.
 */
const getRows = (data?: Apuracao[]): GridRowsProp => {
    const list = data;

    if (Array.isArray(list) && list.length) {
        return list.map((item) => ({
            id: item.idApuracao,
            dhInclusao: item.dhInclusao,
            dtInicio: item.dtInicio,
            dtFim: item.dtFim,
            cdApuracaoFormatado: item.cdApuracaoFormatado,
            peProcessamento: item.peProcessamento,
            idStatusApuracao: item.statusApuracao.idStatusApuracao,
            dsStatusApuracao: item.statusApuracao.dsStatusApuracao,
            idStatusApuracaoRelatorio: item.statusApuracaoRelatorio.idStatusApuracao,
            dsStatusApuracaoRelatorio: item.statusApuracaoRelatorio.dsStatusApuracao,
            dsLink: item.arquivo?.dsLink,
            apuracaoLojasStatusFinalizadoErro: item.apuracaoLojasStatusFinalizadoErro,
        }));
    }

    return [];
};

const columns: GridColDef[] = [
    {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        renderCell: ({ value: isExpanded, row }) => (
            <>
                {row.apuracaoLojasStatusFinalizadoErro.length > 0 && (
                    <IconButton size="small" tabIndex={-1}>
                        {isExpanded ? <Remove /> : <Add />}
                    </IconButton>
                )}
            </>
        ),
    },
    { field: 'cdApuracaoFormatado', headerName: 'Código da apuração', width: 120, sortable: false },
    {
        field: 'dhInclusao',
        headerName: 'Data de geração',
        width: 130,
        sortable: false,
        valueFormatter: (value) => brlDateTime(value),
    },
    { field: 'dtInicio', headerName: 'Data de início', width: 100, sortable: false, valueFormatter: (value) => brlDate(`${value}T00:00:00`) },
    { field: 'dtFim', headerName: 'Data de fim', width: 100, sortable: false, valueFormatter: (value) => brlDate(`${value}T00:00:00`) },
    {
        field: 'status',
        align: 'center',
        headerAlign: 'center',
        headerName: 'Status',
        width: 170,
        sortable: false,
        renderCell: ({ row }) => (
            <GerarReembolsoStatusChip
                idStatusApuracao={row.idStatusApuracao}
                dsStatusApuracao={row.dsStatusApuracao}
                peProcessamento={row.peProcessamento}
            />
        ),
    },
    {
        field: 'statusRelatorio',
        align: 'center',
        headerAlign: 'center',
        headerName: 'Geração do relatório analítico',
        width: 170,
        sortable: false,
        renderCell: ({ row }) => (
            <GerarReembolsoStatusChip idStatusApuracao={row.idStatusApuracaoRelatorio} dsStatusApuracao={row.dsStatusApuracaoRelatorio} />
        ),
    },
    {
        field: 'reverter',
        align: 'center',
        headerAlign: 'center',
        headerName: 'Reverter apuração',
        sortable: false,
        width: 100,
        renderCell: ({ row }) => <ManutencaoReembolsoReverterButton idApuracao={row.id} idStatusApuracao={row.idStatusApuracao} />,
    },
    {
        field: 'download',
        headerName: 'Download',
        sortable: false,
        flex: 1,
        minWidth: 140,
        renderCell: ({ row }) => (
            <ManutencaoReembolsoDownload
                idApuracao={row.id}
                idStatusApuracao={row.idStatusApuracao}
                idStatusApuracaoRelatorio={row.idStatusApuracaoRelatorio}
                dsLink={row.dsLink}
                cdApuracaoFormatado={row.cdApuracaoFormatado}
            />
        ),
    },
];

export function ManutencaoReembolsoGrid() {
    const { data, refetch, isFetching } = useQueryApuracoes();

    return (
        <Block>
            <PanelGrid
                isTreeData
                rows={getRows(data?.data)}
                rowsCount={data?.total ?? 0}
                reload={refetch}
                columns={columns}
                loading={isFetching}
                getDetailPanelHeight={() => 'auto'}
                getDetailPanelContent={({ row }) => (
                    <ListagemProvider>
                        <ManutencaoReembolsoFalhasGrid falhas={row.apuracaoLojasStatusFinalizadoErro} />
                    </ListagemProvider>
                )}
            />
        </Block>
    );
}
